/**
 * 🌐 API 엔드포인트 상수 관리
 * 
 * 모든 API 엔드포인트를 중앙에서 관리
 * 서비스별로 그룹화하여 체계적으로 관리
 */ // ===== Base URLs =====
export var API_BASE_URLS = {
    PRODUCTION: 'https://api.ugot.uk',
    STAGING: 'https://staging-api.ugot.uk',
    DEVELOPMENT: 'http://localhost:8080',
    AUTH_SERVER: 'https://auth.ugot.uk',
    API_GATEWAY: 'https://api.ugot.uk',
    EDUSENSE: 'https://api.ugot.uk',
    USER_SERVER: 'https://api.ugot.uk/api/users',
    SOCIAL_SERVER: 'https://api.ugot.uk/api/social',
    REWARD: 'https://reward.ugot.uk',
    PAY: 'https://pay.ugot.uk',
    KAFKA_UI: 'https://kafkaui.ugot.uk',
    MANAGEMENT: 'https://edusense-mgmt.ugot.uk',
    REDIS_INSIGHT: 'https://redis.ugot.uk'
};
// ===== Auth Endpoints =====
export var AUTH_ENDPOINTS = {
    LOGIN: '/api/auth/login',
    LOGOUT: '/api/auth/logout',
    REFRESH: '/api/auth/refresh',
    VERIFY: '/api/auth/verify',
    OAUTH2: {
        REDIRECT: '/oauth2/redirect',
        CALLBACK: '/oauth2/callback',
        AUTHORIZE: '/oauth2/authorize',
        TOKEN: '/oauth2/token',
        REVOKE: '/oauth2/revoke'
    },
    SOCIAL: {
        GOOGLE: '/api/auth/google',
        GITHUB: '/api/auth/github',
        FACEBOOK: '/api/auth/facebook'
    }
};
// ===== User Endpoints =====
export var USER_ENDPOINTS = {
    PROFILE: '/api/users/profile',
    PROFILE_BY_ID: function(id) {
        return "/api/users/".concat(id, "/profile");
    },
    PROFILE_BY_UUID: function(uuid) {
        return "/api/users/uuid/".concat(uuid, "/profile");
    },
    UPDATE_PROFILE: '/api/users/profile',
    SETTINGS: '/api/users/settings',
    PREFERENCES: '/api/users/preferences',
    AVATAR: '/api/users/avatar',
    EMAIL: '/api/users/email'
};
// ===== Post/Feed Endpoints =====
export var FEED_ENDPOINTS = {
    LIST: '/api/posts',
    CREATE: '/api/posts',
    GET_BY_ID: function(id) {
        return "/api/posts/".concat(id);
    },
    UPDATE: function(id) {
        return "/api/posts/".concat(id);
    },
    DELETE: function(id) {
        return "/api/posts/".concat(id);
    },
    LIKE: function(id) {
        return "/api/posts/".concat(id, "/like");
    },
    UNLIKE: function(id) {
        return "/api/posts/".concat(id, "/unlike");
    },
    COMMENT: function(id) {
        return "/api/posts/".concat(id, "/comments");
    },
    SHARE: function(id) {
        return "/api/posts/".concat(id, "/share");
    }
};
// ===== Course/Learning Endpoints =====
export var COURSE_ENDPOINTS = {
    LIST: '/api/courses',
    GET_BY_ID: function(id) {
        return "/api/courses/".concat(id);
    },
    ENROLL: function(id) {
        return "/api/courses/".concat(id, "/enroll");
    },
    PROGRESS: function(id) {
        return "/api/courses/".concat(id, "/progress");
    },
    COMPLETE: function(id) {
        return "/api/courses/".concat(id, "/complete");
    },
    LESSONS: function(courseId) {
        return "/api/courses/".concat(courseId, "/lessons");
    },
    LESSON_BY_ID: function(courseId, lessonId) {
        return "/api/courses/".concat(courseId, "/lessons/").concat(lessonId);
    }
};
// ===== Project Endpoints =====
export var PROJECT_ENDPOINTS = {
    LIST: '/api/projects',
    CREATE: '/api/projects',
    GET_BY_ID: function(id) {
        return "/api/projects/".concat(id);
    },
    UPDATE: function(id) {
        return "/api/projects/".concat(id);
    },
    DELETE: function(id) {
        return "/api/projects/".concat(id);
    },
    UPLOAD: '/api/projects/upload',
    FORK: function(id) {
        return "/api/projects/".concat(id, "/fork");
    },
    STAR: function(id) {
        return "/api/projects/".concat(id, "/star");
    }
};
// ===== Notification Endpoints =====
export var NOTIFICATION_ENDPOINTS = {
    LIST: '/api/notifications',
    DETAIL: function(id) {
        return "/api/notifications/".concat(id);
    },
    READ_ALL: '/api/notifications/read/all',
    SSE_SUBSCRIBE: function(userId) {
        return "/api/notifications/sse/subscribe/".concat(userId);
    }
};
// ===== Chat/Message Endpoints =====
export var CHAT_ENDPOINTS = {
    CONVERSATIONS: '/api/chat/conversations',
    MESSAGES: function(conversationId) {
        return "/api/chat/conversations/".concat(conversationId, "/messages");
    },
    SEND: function(conversationId) {
        return "/api/chat/conversations/".concat(conversationId, "/send");
    },
    TYPING: function(conversationId) {
        return "/api/chat/conversations/".concat(conversationId, "/typing");
    },
    READ: function(conversationId) {
        return "/api/chat/conversations/".concat(conversationId, "/read");
    }
};
// ===== Admin Endpoints =====
export var ADMIN_ENDPOINTS = {
    USERS: '/api/admin/users',
    USER_BY_ID: function(id) {
        return "/api/admin/users/".concat(id);
    },
    USER_BY_USERNAME: function(username) {
        return "/api/admin/users/".concat(username);
    },
    TEST_USERS: '/api/admin/test-users',
    TEST_USERS_BATCH: '/api/admin/test-users/batch',
    POSTS: '/api/admin/posts',
    ANALYTICS: '/api/admin/analytics',
    REPORTS: '/api/admin/reports',
    SETTINGS: '/api/admin/settings'
};
// ===== Health/Status Endpoints =====
export var HEALTH_ENDPOINTS = {
    HEALTH: '/health',
    READY: '/ready',
    LIVE: '/live',
    METRICS: '/metrics',
    STATUS: '/api/status'
};
// ===== WebSocket Endpoints =====
export var WEBSOCKET_ENDPOINTS = {
    CHAT: '/ws/chat',
    NOTIFICATIONS: '/ws/notifications',
    PRESENCE: '/ws/presence',
    COLLABORATION: '/ws/collaboration'
};
// ===== Proxy Endpoints (for OAuth) =====
export var PROXY_ENDPOINTS = {
    TOKEN: '/api/proxy/token',
    USERINFO: '/api/proxy/userinfo',
    INTROSPECT: '/api/proxy/introspect'
};
// ===== Debug / Diagnostics Endpoints (AuthServer) =====
export var DEBUG_ENDPOINTS = {
    HEADERS: '/_debug/headers',
    CREATE_TEST_USER: '/_debug/create-test-user',
    TRIGGER_USER_EVENT: '/_debug/trigger-user-event',
    OAUTH2_CLEAR_AUTHORIZATIONS: '/_debug/oauth2/authorizations',
    OAUTH2_CLEAR_ALL: '/_debug/oauth2/all',
    OAUTH2_AUTH_COUNT: '/_debug/oauth2/authorizations/count',
    OAUTH2_CLIENTS: '/_debug/oauth2/clients',
    ADMIN_POST_TEST: '/_debug/admin-post-test',
    ADMIN_GET_TEST: '/_debug/admin-get-test'
};
// Export all endpoints in one object for convenience
export var API_ENDPOINTS = {
    auth: AUTH_ENDPOINTS,
    user: USER_ENDPOINTS,
    feed: FEED_ENDPOINTS,
    course: COURSE_ENDPOINTS,
    project: PROJECT_ENDPOINTS,
    notification: NOTIFICATION_ENDPOINTS,
    chat: CHAT_ENDPOINTS,
    admin: ADMIN_ENDPOINTS,
    health: HEALTH_ENDPOINTS,
    websocket: WEBSOCKET_ENDPOINTS,
    proxy: PROXY_ENDPOINTS,
    debug: DEBUG_ENDPOINTS
};
